<template>
  <div v-if="series && series.length > 1" class="box evo-chart-block">
    <div class="evo-chart-head">
      <div class="evo-chart-head-text">
        <h2>Evolución del Clima total</h2>
        <p>
          Cada punto es un proceso. El fondo son las seis bandas de calidad de
          clima. Haz clic en un chip para mostrar u ocultar esa serie.
        </p>
      </div>
      <div class="evo-toggle-row">
        <button
          v-for="g in groups"
          :key="g.key"
          type="button"
          class="evo-toggle"
          :class="{ on: !hiddenKeys[g.key] }"
          :style="{ color: g.color }"
          @click="toggleGroup(g.key)"
        >
          <span class="evo-toggle-dot" :style="{ background: g.color }"></span>
          {{ g.label }}
        </button>
      </div>
    </div>
    <div class="evo-chart-canvas-wrap">
      <GraphChartJs :config="chartConfig" :cambio="renderCambio" />
    </div>
  </div>
</template>

<script>
import GraphChartJs from "../../dashboard/GraphChartJs.vue";

const BANDS = [
  { yMin: 84, yMax: 100, color: "rgba(118,169,138,0.647)", label: "Sigamos así" },
  { yMin: 70, yMax: 84, color: "rgba(159,193,134,0.647)", label: "Vamos bien" },
  { yMin: 50, yMax: 70, color: "rgba(197,213,142,0.647)", label: "Estamos cerca" },
  { yMin: 30, yMax: 50, color: "rgba(212,200,159,0.647)", label: "Cuidado" },
  { yMin: 16, yMax: 30, color: "rgba(210,150,132,0.647)", label: "Necesita atención" },
  { yMin: 0, yMax: 16, color: "rgba(197,118,113,0.647)", label: "Actuemos ya" },
];
const BAND_TICKS = [0, 16, 30, 50, 70, 84, 100];
const CURRENT_COLOR = "#522687";

const EDGE_MARGIN = 60;
const RIGHT_LABEL_SPACE = 70;

function roundedRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const GROUPS = [
  { key: "CClimad", label: "Clima total", color: "#522687", hidden: false },
  { key: "engad", label: "Engagement", color: "#2b2b2b", hidden: false },
  { key: "apoyod", label: "Apoyo", color: "#15b7b9", hidden: true },
  { key: "innovaciond", label: "Innovación", color: "#9246df", hidden: true },
  { key: "calidadd", label: "Calidad", color: "#07689f", hidden: true },
  { key: "metasd", label: "Metas", color: "#c9860e", hidden: true },
];

const chartDecorationsPlugin = {
  beforeDatasetsDraw(chart) {
    const yScale = chart.scales["y-axis-0"];
    const xScale = chart.scales["x-axis-0"];
    if (!yScale || !xScale) return;
    const ctx = chart.ctx;
    const left = xScale.left - EDGE_MARGIN;
    const right = xScale.right + EDGE_MARGIN;

    const top = yScale.getPixelForValue(100);
    const bottom = yScale.getPixelForValue(0);
    ctx.save();
    roundedRectPath(ctx, left, top, right - left, bottom - top, 8);
    ctx.clip();
    BANDS.forEach((b) => {
      const yTop = yScale.getPixelForValue(b.yMax);
      const yBottom = yScale.getPixelForValue(b.yMin);
      ctx.fillStyle = b.color;
      ctx.fillRect(left, yTop, right - left, yBottom - yTop);
    });
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.lineWidth = 1;
    chart.data.labels.forEach((label, i) => {
      const x = xScale.getPixelForTick(i);
      ctx.beginPath();
      ctx.moveTo(x, yScale.top);
      ctx.lineTo(x, yScale.bottom);
      ctx.stroke();
    });
    ctx.restore();
  },

  afterDatasetsDraw(chart) {
    const yScale = chart.scales["y-axis-0"];
    const xScale = chart.scales["x-axis-0"];
    if (!yScale || !xScale) return;
    const ctx = chart.ctx;
    const lastIndex = chart.data.labels.length - 1;

    ctx.save();
    ctx.font = "11px sans-serif";
    ctx.fillStyle = "#55545a";
    ctx.textBaseline = "middle";
    BANDS.forEach((b) => {
      const y = (yScale.getPixelForValue(b.yMin) + yScale.getPixelForValue(b.yMax)) / 2;
      ctx.fillText(b.label, xScale.right + EDGE_MARGIN + 8, y);
    });
    ctx.restore();

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    chart.data.labels.forEach((label, i) => {
      const isCurrent = i === lastIndex;
      ctx.font = isCurrent ? "bold 12.5px sans-serif" : "12.5px sans-serif";
      ctx.fillStyle = isCurrent ? CURRENT_COLOR : "#55545a";
      ctx.fillText(String(label), xScale.getPixelForTick(i), xScale.bottom + 8);
    });
    ctx.restore();

    const primaryIndex = chart.data.datasets.findIndex((d) => d.key === "CClimad");
    if (primaryIndex === -1 || !chart.isDatasetVisible(primaryIndex)) return;
    const meta = chart.getDatasetMeta(primaryIndex);
    if (!meta) return;

    ctx.save();
    ctx.font = "bold 11px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    meta.data.forEach((point, i) => {
      const value = chart.data.datasets[primaryIndex].data[i];
      const text = `${value.toFixed(1).replace(".", ",")}%`;
      const isLast = i === meta.data.length - 1;
      const bw = 10 + text.length * 6.4;
      const bh = 20;
      const bx = point._model.x;
      const by = point._model.y - (isLast ? 26 : 22);

      ctx.shadowColor = "rgba(20,20,20,0.18)";
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 1;
      ctx.fillStyle = isLast ? CURRENT_COLOR : "#fff";
      roundedRectPath(ctx, bx - bw / 2, by - bh / 2, bw, bh, 6);
      ctx.fill();

      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      ctx.strokeStyle = isLast ? CURRENT_COLOR : "rgba(0,0,0,0.12)";
      ctx.lineWidth = 1;
      roundedRectPath(ctx, bx - bw / 2, by - bh / 2, bw, bh, 6);
      ctx.stroke();

      ctx.fillStyle = isLast ? "#fff" : "#363636";
      ctx.fillText(text, bx, by);
    });
    ctx.restore();
  },
};

export default {
  name: "EvolutionChart",

  components: {
    GraphChartJs,
  },

  props: {
    series: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      groups: GROUPS,
      hiddenKeys: GROUPS.reduce((acc, g) => {
        acc[g.key] = g.hidden;
        return acc;
      }, {}),
      renderTick: 0,
    };
  },

  computed: {
    renderCambio() {
      return this.series.length * 10000 + this.renderTick;
    },

    chartConfig() {
      const labels = this.series.map((pt) =>
        pt.fecha ? new Date(pt.fecha).getFullYear() : "",
      );

      const datasets = GROUPS.map((g) => ({
        key: g.key,
        label: g.label,
        data: this.series.map((pt) => (pt.total && pt.total[g.key] ? pt.total[g.key].p : 0)),
        borderColor: g.color,
        backgroundColor: g.color,
        hidden: this.hiddenKeys[g.key],
        borderWidth: g.key === "CClimad" ? 3 : 2,
        fill: false,
        tension: 0,
        pointBackgroundColor: g.color,
        pointBorderColor: "#fff",
        pointRadius: (context) =>
          context.dataIndex === this.series.length - 1 ? 5 : 3,
      }));

      return {
        type: "line",
        data: { labels, datasets },
        plugins: [chartDecorationsPlugin],
        options: {
          maintainAspectRatio: false,
          layout: {
            padding: {
              left: 4,
              right: EDGE_MARGIN + RIGHT_LABEL_SPACE,
              top: 26,
              bottom: 24,
            },
          },
          legend: { display: false },
          tooltips: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: (item, data) =>
                `${data.datasets[item.datasetIndex].label}: ${Number(item.yLabel)
                  .toFixed(1)
                  .replace(".", ",")}%`,
            },
          },
          scales: {
            yAxes: [
              {
                id: "y-axis-0",
                ticks: { min: 0, max: 100, padding: EDGE_MARGIN },
                afterBuildTicks: (scale) => {
                  scale.ticks = BAND_TICKS.slice().reverse();
                },
                gridLines: { display: false },
              },
            ],
            xAxes: [
              {
                id: "x-axis-0",
                ticks: { display: false },
                gridLines: { display: false },
              },
            ],
          },
        },
      };
    },
  },

  methods: {
    toggleGroup(key) {
      this.$set(this.hiddenKeys, key, !this.hiddenKeys[key]);
      this.renderTick++;
    },
  },
};
</script>

<style scoped>
.evo-chart-block {
  padding: 1.6rem 1.8rem 1.2rem;
  margin-top: 18px;
}

.evo-chart-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.evo-chart-head-text {
  min-width: 0;
  flex: 1 1 260px;
}

.evo-chart-head h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.evo-chart-head p {
  font-size: 12px;
  color: #7a7a7a;
  margin: 2px 0 0;
}

.evo-toggle-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  flex: 0 1 auto;
}

.evo-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 20px;
  padding: 5px 12px 5px 8px;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.15s ease, border-color 0.15s ease;
}

.evo-toggle:hover {
  opacity: 0.85;
}

.evo-toggle.on {
  opacity: 1;
  border-color: currentColor;
}

.evo-toggle-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: none;
}

.evo-chart-canvas-wrap {
  position: relative;
  height: 380px;
  margin-top: 10px;
}

.evo-chart-canvas-wrap >>> > div {
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/climan/components/evolutionChart.vue